<template>
  <div class="trend-percent" :class="[isEmpty(position) ? '' : 'absolute-el']" :flex="flex" :style="{ ...positionStyle }">
    <span class="label" :style="[label?.followValueChange ? percentStyle : labelStyle]" v-if="label?.show">{{ label?.value }}</span>
    <div
      class="percent"
      flex="main:justify cross:center"
      :style="{
        ...percentStyle,
        '--iconImg': `url('${percentStyle.iconImg}')` as string,
        '--before': base.iconPosition === 'before' ? ('inline-block' as string) : ('none' as string),
        '--after': base.iconPosition === 'after' ? ('inline-block' as string) : ('none' as string),
      }"
    >
      <span v-if="base.showSign" style="max-width: 8px">{{ percentValue ? percentValue.sign : "" }}</span>
      <span class="value">{{ percentValue ? percentValue.percent : 0 }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed, toRef } from "vue"
import { Position, TextStyle, TrendPercentOptions, StoreDataType, StateMap, PercentValue } from "./trend-percent.d"
import { isEmpty } from "lodash-es"
// 定义props
const props = withDefaults(
  defineProps<{
    position?: Position
    options: TrendPercentOptions
    data?: StoreDataType | null
    flex?: string
  }>(),
  {
    position: () => ({}),
    options: () => ({
      label: {
        show: false,
        value: "",
        color: "#000",
      },
      base: {
        fontSize: "12px",
        fontFamily: "PingFang SC",
        color: "#000",
        iconPosition: "before",
        showSign: true,
      },
    }),
    data: () => ({
      value: undefined,
    }),
    flex: () => "main:left cross:center",
  },
)

// 计算位置的样式
const positionStyle = props.position

// 解构获取配置信息
const { label, base } = props.options
const labelStyle = computed<TextStyle>(() => {
  return {
    color: label?.color,
  }
})
const dataRef = toRef(props, "data")

// percent值，将符号和百分比数值分开返回
const percentValue = computed<PercentValue>(() => {
  if (dataRef.value) {
    const { value } = dataRef.value
    const STATE_SIGN_MAP: StateMap = {
      negative: "-",
      positive: "+",
      base: "",
    }
    if (value === undefined) {
      return {
        sign: "",
        percent: "--",
      }
    }
    const state = calcPercentLevel(value)
    const percent = state === "negative" ? (value + "").replace(/-/g, "") : value
    return {
      sign: STATE_SIGN_MAP[state],
      percent: percent + "%",
    }
  }
  return {
    sign: "",
    percent: "--",
  }
})

/**
 * 根据数值，计算是负趋势还是正趋势，还是零
 * @param percent
 */
function calcPercentLevel(percent: number): string {
  if (percent < 0) {
    return "negative"
  } else if (percent > 0) {
    return "positive"
  }
  return "base"
}

type StyleMap = {
  [key: string]: TextStyle
}

// 计算percent显示的样式
const percentStyle = computed<TextStyle>(() => {
  const { positive, negative, base: baseConfig } = props.options
  const base: TextStyle = {
    fontSize: baseConfig.fontSize,
    fontFamily: baseConfig.fontFamily,
    color: baseConfig.color,
  }

  const styleMap: StyleMap = {
    base,
    negative,
    positive,
  }

  if (!dataRef.value) {
    return base
  }

  const { value } = dataRef.value || 0
  const state: keyof StyleMap = calcPercentLevel(value)
  const stateStyle = styleMap[state]
  return stateStyle
})
</script>

<style lang="scss">
@mixin iconImg {
  content: "";
  width: 16px;
  height: 16px;
  background-image: var(--iconImg，none);
}

.trend-percent {
  .percent::before {
    @include iconImg;
    display: var(--before);
  }

  .percent::after {
    @include iconImg;
    display: var(--after);
  }

  .value {
    white-space: nowrap;
  }
}
</style>
